import React, { JSX } from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Stack,
  Text,
  withTranslation,
  Spinner,
  Card,
} from '@piwikpro/ui-components';
import { OrganizationState } from '@piwikpro/organization-crate';
import { AuthSettings } from '@piwikpro/auth-crate';
import { Formik } from 'formik';
import FailMessage from '../FailMessage';

export interface TechnicalSupportProps {
  t: (key: string) => string
  supportUserAccess?: {
    enabled: boolean
  }
  isLoading: boolean
  failed: boolean
  authSettings: AuthSettings
  license: OrganizationState['details']['license']['current']
}
const fieldName = 'support_user_access';

interface SwitchHandlerValue {
  [fieldName]: boolean
}

const CardWrapper = ({ children }: {
  children: React.ReactNode
}): JSX.Element => (
  <Stack.Item fill dataId="TechnicalSupportSettings">
    <Card>
      <Card.Section>
        {children}
      </Card.Section>
    </Card>
  </Stack.Item>
);

const TechnicalSupport: React.FunctionComponent<TechnicalSupportProps> = ({
  t,
  supportUserAccess,
  isLoading,
  failed,
  authSettings,
  license,
}) => {
  const isAnyEnterpriseClient = license && license.plan.id !== 'trial' && license.plan.id !== 'business';
  const handleSubmitSwitcher = async (
    values: SwitchHandlerValue,
    { setSubmitting, setFieldValue }: Formik,
  ) => {
    try {
      await authSettings.setSupportUserAccess({ enabled: values[fieldName] });
      await authSettings.getSettingsForOrganization();
    } catch (err) {
      setFieldValue(
        fieldName,
        !values[fieldName],
      );
    }
    setSubmitting(false);
  };

  if (isLoading) {
    return (
      <CardWrapper>
        <Spinner />
      </CardWrapper>
    );
  }

  if (failed) {
    return (
      <CardWrapper>
        <FailMessage />
      </CardWrapper>
    );
  }

  if (supportUserAccess?.enabled === undefined) return null;

  return (
    <CardWrapper>
      <Stack wrap verticalAlignment="center">
        <Stack.Item fill>
          <Stack vertical spacing="narrow">
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('accountSecurity.technicalSupport.allow-technical-support-access.title')}</Text>
            </Stack.Item>
            <Stack.Item>
              <Text as="p">{t('accountSecurity.technicalSupport.allow-technical-support-access.description')}</Text>
            </Stack.Item>
          </Stack>
          {
            isAnyEnterpriseClient && <Text as="p">{t('accountSecurity.technicalSupport.allow-technical-support-access.description-paying')}</Text>
          }
        </Stack.Item>
        <Stack.Item>
          <Action.AutoSavedSwitcherControl
            id="support-user-access"
            initialValue={supportUserAccess?.enabled}
            name={fieldName}
            onSubmit={handleSubmitSwitcher}
          />
        </Stack.Item>
      </Stack>
    </CardWrapper>
  );
};

export default withInject({
  authSettings: 'AuthCrate.authSettings',
})(
  withTranslation(['administration'])(TechnicalSupport),
);
